import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, Input, PageHeader, Select, Spinner, statusTone, Table } from "../components/ui";
import { STATUS_LABELS, fmtDate, fmtPct, cls } from "../lib/format";

type TestRow = {
  id: string;
  name: string;
  status: string;
  type: string;
  sendingMethod: string;
  createdAt: string;
  latestRunId: string | null;
  options?: { summary?: { inboxPct: number; spamPct: number; categoryPct: number; total: number } };
};

export function TestsList() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["tests", search, status],
    queryFn: () => api<{ items: TestRow[] }>(`/tests?search=${encodeURIComponent(search)}&status=${status}`),
  });
  const deleteTest = useMutation({
    mutationFn: (id: string) => api(`/tests/${id}`, { method: "DELETE", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tests"] }),
  });
  const runAgain = useMutation({
    mutationFn: (id: string) => api(`/tests/${id}/run-again`, { method: "POST", body: "{}" }),
    onSuccess: (run: any) => {
      qc.invalidateQueries({ queryKey: ["tests"] });
      navigate(`/tests/${run.testId}/runs/${run.id}`);
    },
  });
  const duplicate = useMutation({
    mutationFn: (id: string) => api(`/tests/${id}/duplicate`, { method: "POST", body: "{}" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tests"] });
      navigate("/tests");
    },
  });

  return (
    <div>
      <PageHeader
        title="Inbox Placement"
        subtitle="Test placement across your seed mailbox pool"
        actions={
          <Button onClick={() => navigate("/tests/new")}>
            <span className="text-base leading-none">+</span> Add New
          </Button>
        }
      />
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-ink-100 p-4">
          <Input placeholder="Search tests…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="max-w-[180px]">
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
        </div>
        {isLoading ? (
          <Spinner />
        ) : !data?.items.length ? (
          <div className="p-6">
            <EmptyState title="No tests yet" description="Create your first inbox placement test to measure deliverability." action={<Link to="/tests/new"><Button>Create test</Button></Link>} />
          </div>
        ) : (
          <Table headers={["Name", "Status", "Inbox", "Spam", "Category", "Type", "Sending Method", "Created", "Actions"]}>
            {data!.items.map((t) => {
              const s = t.options?.summary;
              return (
                <tr key={t.id} className="table-row-hover">
                  <td className="px-4 py-3">
                    <Link to={t.latestRunId ? `/tests/${t.id}/runs/${t.latestRunId}` : `/tests/new?edit=${t.id}`} className="font-medium text-ink-800 hover:text-brand-600">
                      {t.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><Badge tone={statusTone(t.status)}>{STATUS_LABELS[t.status] ?? t.status}</Badge></td>
                  <td className="px-4 py-3 tabular-nums">{s ? fmtPct(s.inboxPct) : "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{s ? fmtPct(s.spamPct) : "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{s ? fmtPct(s.categoryPct) : "—"}</td>
                  <td className="px-4 py-3 capitalize">{t.type === "one_time" ? "One-time" : "Automated"}</td>
                  <td className="px-4 py-3 capitalize">{t.sendingMethod.replace("_", " ")}</td>
                  <td className="px-4 py-3 text-ink-500">{fmtDate(t.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {t.latestRunId && (
                        <Link to={`/tests/${t.id}/runs/${t.latestRunId}`} className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50">
                          Report
                        </Link>
                      )}
                      <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => duplicate.mutate(t.id)} title="Duplicate">⧉</button>
                      <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => runAgain.mutate(t.id)} title="Run again">↻</button>
                      <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete "${t.name}"?`)) deleteTest.mutate(t.id); }} title="Delete">✕</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>
    </div>
  );
}
